'use client'

import * as React from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { MapPin } from 'lucide-react'
import { cn } from '~/shared/common/utils'
import { Input } from '~/client/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '~/client/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '~/client/components/ui/command'
import GoogleLogoIcon from '../icons/GoogleLogoIcon'

// Google Places API types
interface PlacePrediction {
  place_id: string
  description: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
}

interface PlaceDetails {
  place_id?: string
  formatted_address?: string
  geometry?: {
    location: {
      lat(): number
      lng(): number
    }
  }
  name?: string
  address_components?: Array<{
    long_name: string
    short_name: string
    types: string[]
  }>
  types?: string[]
}

interface GoogleMapsAutocompleteProps {
  value?: string
  onChange?: (value: string, placeDetails?: PlaceDetails) => void
  onPlaceSelect?: (placeDetails: PlaceDetails) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  apiKey?: string
  types?: string[]
  componentRestrictions?: google.maps.places.ComponentRestrictions
  bounds?: google.maps.LatLngBounds
  strictBounds?: boolean
  fields?: string[]
  onError?: (error: Error) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

// Inner component that uses the Google Maps library
function GoogleMapsAutocompleteInner({
  value = '',
  onChange,
  onPlaceSelect,
  placeholder = 'Search for a location...',
  className,
  disabled = false,
  types,
  componentRestrictions,
  bounds,
  strictBounds = false,
  fields = ['place_id', 'formatted_address', 'geometry', 'name', 'address_components', 'types'],
  onError,
  onKeyDown,
}: GoogleMapsAutocompleteProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [predictions, setPredictions] = React.useState<PlacePrediction[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(value)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined)

  // Use the Google Maps library from @vis.gl/react-google-maps
  const places = useMapsLibrary('places')

  // Update input value when prop changes
  React.useEffect(() => {
    setInputValue(value)
  }, [value])

  // Open popover when predictions arrive
  React.useEffect(() => {
    if (predictions.length > 0) {
      setIsOpen(true)
    }
  }, [predictions])

  // Search Google Places
  const searchGooglePlaces = React.useCallback(
    async (query: string): Promise<PlacePrediction[]> => {
      if (!places || !query.trim()) {
        return []
      }

      const request: any = {
        input: query,
      }
      if (types) request.includedPrimaryTypes = types
      if (componentRestrictions?.country) {
        const countries = Array.isArray(componentRestrictions.country)
          ? componentRestrictions.country
          : [componentRestrictions.country]
        request.includedRegionCodes = countries
      }
      if (bounds && strictBounds) {
        request.locationRestriction = bounds
      } else if (bounds) {
        request.locationBias = bounds
      }

      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

        return suggestions
          .filter((s) => s.placePrediction)
          .map((suggestion) => ({
            place_id: suggestion.placePrediction!.placeId,
            description: suggestion.placePrediction!.text.text || '',
            structured_formatting: {
              main_text: suggestion.placePrediction!.text.text?.split(',')[0]?.trim() || '',
              secondary_text: suggestion.placePrediction!.text.text?.split(',').slice(1).join(',').trim() || '',
            },
          }))
      } catch (error) {
        console.error('Failed to fetch Google autocomplete suggestions:', error)
        onError?.(error as Error)
        return []
      }
    },
    [places, types, componentRestrictions, bounds, strictBounds, onError]
  )

  // Debounced search function using new AutocompleteSuggestion API
  const searchPlaces = React.useCallback(
    async (query: string) => {
      if (!places || !query.trim()) {
        setPredictions([])
        setIsOpen(false)
        return
      }

      setIsLoading(true)

      try {
        const placePredictions = await searchGooglePlaces(query)
        setPredictions(placePredictions)
      } catch (error) {
        console.error('Failed to fetch autocomplete suggestions:', error)
        setPredictions([])
        setIsOpen(false)
        onError?.(error as Error)
      } finally {
        setIsLoading(false)
      }
    },
    [places, searchGooglePlaces, onError]
  )

  // Handle input change with debouncing
  const handleInputChange = (newValue: string) => {
    setInputValue(newValue)
    onChange?.(newValue)

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout for debounced search
    timeoutRef.current = setTimeout(() => {
      searchPlaces(newValue)
    }, 300)
  }

  // Handle place selection using new Place API
  const handlePlaceSelect = React.useCallback(
    async (prediction: PlacePrediction) => {
      if (!places) return

      try {
        // Create a new Place instance with the place_id
        const place = new places.Place({
          id: prediction.place_id,
        })

        // Map old field names to new Google Places API field names
        const fieldMapping: Record<string, string> = {
          place_id: 'id',
          formatted_address: 'formattedAddress',
          geometry: 'location',
          name: 'displayName',
          address_components: 'addressComponents',
          types: 'types',
        }

        // Convert the fields to new API field names
        const googlePlaceFields = fields.map((field) => fieldMapping[field] || field)

        // Fetch the requested fields using new field names
        await place.fetchFields({ fields: googlePlaceFields })

        // Map the new Place object to the existing PlaceDetails interface
        const placeDetails: PlaceDetails = {
          place_id: place.id,
          formatted_address: place.formattedAddress ?? undefined,
          geometry: place.location
            ? {
              location: {
                lat: () => place.location!.lat(),
                lng: () => place.location!.lng(),
              },
            }
            : undefined,
          name: place.displayName ?? undefined,
          address_components: place.addressComponents?.map((component) => ({
            long_name: component.longText || '',
            short_name: component.shortText || '',
            types: component.types || [],
          })),
          types: (place as any).types ?? undefined,
        }

        setInputValue(prediction.description)
        onChange?.(prediction.description, placeDetails)
        onPlaceSelect?.(placeDetails)
        setIsOpen(false)
        setPredictions([])
      } catch (error) {
        console.error('Failed to fetch place details:', error)
        onError?.(error as Error)
      }
    },
    [places, fields, onChange, onPlaceSelect, onError]
  )

  const [selectedIndex, setSelectedIndex] = React.useState(-1)

  // Reset selected index when predictions change
  React.useEffect(() => {
    setSelectedIndex(-1)
  }, [predictions])

  // Handle key navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || predictions.length === 0) {
        onKeyDown?.(e)
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % predictions.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + predictions.length) % predictions.length)
      } else if (e.key === 'Enter') {
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          e.preventDefault()
          handlePlaceSelect(predictions[selectedIndex])
        } else if (predictions.length > 0) {
          e.preventDefault()
          handlePlaceSelect(predictions[0])
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false)
      } else {
        onKeyDown?.(e)
      }
    },
    [isOpen, predictions, selectedIndex, handlePlaceSelect, onKeyDown]
  )

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            className={cn('pl-9 pr-10', className)}
            disabled={disabled}
            onKeyDown={handleKeyDown}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            {isLoading ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" /> : <MapPin className="h-4 w-4 text-gray-400" />}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command>
          <CommandList>
            {predictions.length === 0 && !isLoading ? (
              <CommandEmpty>Type to search</CommandEmpty>
            ) : (
              <CommandGroup>
                {predictions.map((prediction, index) => (
                  <CommandItem
                    key={prediction.place_id}
                    value={prediction.description}
                    onSelect={() => handlePlaceSelect(prediction)}
                    className={cn('flex items-start gap-2 p-3', index === selectedIndex && 'bg-accent text-accent-foreground')}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="font-medium text-sm truncate">{prediction.structured_formatting.main_text}</div>
                      {prediction.structured_formatting.secondary_text && <div className="text-xs text-gray-500 truncate">{prediction.structured_formatting.secondary_text}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        <div className="flex items-center justify-end gap-1 px-3 py-2 border-t bg-muted/30">
          <span className="text-[10px] text-muted-foreground">powered by</span>
          <GoogleLogoIcon />
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Hook for easier usage
export function useGoogleMapsAutocomplete(initialValue = '') {
  const [value, setValue] = React.useState(initialValue)
  const [placeDetails, setPlaceDetails] = React.useState<PlaceDetails | undefined>()

  const handleChange = React.useCallback((newValue: string, details?: PlaceDetails) => {
    setValue(newValue)
    setPlaceDetails(details)
  }, [])

  return {
    value,
    placeDetails,
    onChange: handleChange,
    setValue,
    setPlaceDetails,
  }
}

// Main component - expects to be wrapped in APIProvider by parent
export function GoogleMapsAutocomplete(props: GoogleMapsAutocompleteProps) {
  return <GoogleMapsAutocompleteInner {...props} />
}
